import React from 'react';
import { CirclesIcon, BriefcaseIcon, LockIcon } from './Icons';
import styles from '../styles/HeaderNav.module.css';

/**
 * Top-of-app nav: You · Circles · Coach (a slot drops out entirely when its
 * feature is switched off — ENABLE_CIRCLES / ENABLE_COACHES — shrinking the
 * nav to 2 slots, or just "You").
 *  - You / Circles switch the main chat between the personal bracelet chat and a
 *    circle group chat; the sliding highlight tracks the active one.
 *  - Coach opens booking (it replaces the old "Book Coach" button) and keeps the
 *    coaching-session status — a dot when a session is scheduled, pulsing green
 *    when it's joinable.
 *
 * Props:
 *  - active: 'you' | 'circles' | 'coach'
 *  - onYou() / onCircles() / onCoach()
 *  - showCircles: boolean — false when ENABLE_CIRCLES is off server-side; the
 *    slot is removed entirely (not just disabled).
 *  - showCoach: boolean — false when ENABLE_COACHES is off server-side; same
 *    removal treatment as showCircles.
 *  - circlesUnread: boolean
 *  - circlesLocked: boolean — no circles + no Sophia Together: the tab still
 *    works (it opens the Sophia Together offer), the lock just signals it's
 *    a premium door.
 *  - coachScheduled / coachJoinable: booleans (session status)
 */
function HeaderNav({
  active = 'you',
  onYou,
  onCircles,
  onCoach,
  showCircles = true,
  showCoach = true,
  circlesUnread = false,
  circlesLocked = false,
  coachScheduled = false,
  coachJoinable = false
}) {
  const slots = 1 + (showCircles ? 1 : 0) + (showCoach ? 1 : 0);
  // Thumb travels in whole-slot steps, so its index is the slot's position in
  // the CURRENT layout — Coach sits after Circles when both are shown.
  const coachIdx = showCircles ? 2 : 1;
  const idx = (active === 'coach' && showCoach) ? coachIdx
    : (active === 'circles' && showCircles) ? 1 : 0;

  return (
    <div className={styles.nav} data-active={active}>
      <span
        className={styles.thumb}
        style={{ width: `calc((100% - 8px) / ${slots})`, transform: `translateX(${idx * 100}%)` }}
        aria-hidden="true"
      />

      <button type="button" className={`${styles.slot} ${styles.you}`} onClick={onYou} aria-label="Your chat">
        <img src="/assets/sophia-s-silver.webp" alt="" className={styles.bead} />
        <span className={styles.label}>You</span>
      </button>

      {showCircles && (
      <button
        type="button"
        className={`${styles.slot} ${styles.circles} ${circlesLocked ? styles.locked : ''}`}
        onClick={onCircles}
        aria-label={circlesLocked ? 'Circles (locked — upgrade to Sophia Together)' : 'Circles'}
      >
        <CirclesIcon size={18} lively={circlesUnread && !circlesLocked} />
        <span className={styles.label}>Circles</span>
        {circlesUnread && <span className={styles.badge} />}
        {circlesLocked && !circlesUnread && (
          <span className={styles.lockBadge} aria-label="Sophia Together required">
            <LockIcon size={9} />
          </span>
        )}
      </button>
      )}

      {showCoach && (
      <button
        type="button"
        className={`${styles.slot} ${styles.coach}`}
        onClick={onCoach}
        data-tour="session-indicator"
        aria-label="Coach"
      >
        <BriefcaseIcon size={17} />
        <span className={styles.label}>Coach</span>
        {coachScheduled && <span className={coachJoinable ? styles.dotLive : styles.dot} />}
      </button>
      )}
    </div>
  );
}

export default HeaderNav;
